import {IconButton} from '@dropbox/dig-components/buttons';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {ChevronDownLine, ChevronRightLine, CloseLine} from '@dropbox/dig-icons/assets';
import type {RoadmapAuditFindingItem} from 'client/models/RoadmapAuditFindingItem';
import type {RoadmapAuditFindingViolation} from 'client/models/RoadmapAuditFindingViolation';
import {Link} from 'components/DSYS/Link';
import type {Column} from 'components/DSYS/Table';
import {Table} from 'components/DSYS/Table';
import {ROUTE_PATHS} from 'constant';
import {formatDateWithYear} from 'helpers/dates';
import {useEffect, useMemo, useRef, useState} from 'react';
import styles from 'views/roadmap/Roadmap.module.css';
import {RoadmapInitiativeLink} from 'views/roadmap/RoadmapInitiativeLink';
import {RoadmapJiraStatus} from 'views/roadmap/RoadmapJiraStatus';
import {RoadmapPerson} from 'views/roadmap/RoadmapPerson';

import {
  type AuditFindingHierarchyNode,
  buildAuditFindingHierarchy,
  getAuditHierarchyNodeIds,
} from './auditHierarchy';

const AUDIT_FINDING_COLUMNS: Column<RoadmapAuditFindingItem>[] = [
  {type: 'initiative', minWidth: 320, spanner: true, cantDisable: true},
  {type: 'dri', minWidth: 160, width: 180, cantDisable: true},
  {type: 'status', minWidth: 130, width: 140, cantDisable: true},
  {type: 'start_date', minWidth: 110, width: 120, cantDisable: true},
  {type: 'end_date', minWidth: 110, width: 120, cantDisable: true},
  {type: 'flag', minWidth: 175, width: 175, maxWidth: 175, fixed: true, cantDisable: true},
];

const AUDIT_FINDING_COLUMN_LABELS = {
  initiative: 'Initiative',
  dri: 'DRI',
  status: 'Status',
  start_date: 'Start Date',
  end_date: 'End Date',
  flag: 'Flags',
};

const AUDIT_FINDING_COLUMN_HEADERS = Object.fromEntries(
  Object.entries(AUDIT_FINDING_COLUMN_LABELS).map(([key, label]) => [
    key,
    <Text key={key} size="small" isBold>
      {label}
    </Text>,
  ])
);

const AUDIT_HIERARCHY_INDENT_PX = 20;
const AUDIT_HIERARCHY_TOGGLE_OFFSET_PX = 30;

type VisibleAuditFindingRow =
  | {
      type: 'group';
      node: AuditFindingHierarchyNode;
      depth: number;
      childrenId: string;
      domId?: string;
    }
  | {
      type: 'item';
      item: RoadmapAuditFindingItem;
      depth: number;
      domId?: string;
    };

const domSafeId = (value: string) =>
  value.replace(/[^a-zA-Z0-9_-]/g, (character) => `-${character.codePointAt(0)}-`);

const flattenVisibleHierarchy = (
  nodes: AuditFindingHierarchyNode[],
  expandedIds: Set<string>,
  depth = 0
): VisibleAuditFindingRow[] =>
  nodes.flatMap((node) => {
    const childrenId = `audit-finding-group-children-${domSafeId(node.id)}`;
    const groupRow: VisibleAuditFindingRow = {type: 'group', node, depth, childrenId};
    if (!expandedIds.has(node.id)) return [groupRow];

    const descendants: VisibleAuditFindingRow[] = [
      ...node.items.map((item): VisibleAuditFindingRow => ({type: 'item', item, depth: depth + 1})),
      ...flattenVisibleHierarchy(node.children, expandedIds, depth + 1),
    ];
    if (descendants[0]) {
      descendants[0] = {...descendants[0], domId: childrenId};
    }
    return [groupRow, ...descendants];
  });

export const formatAuditFindingDate = (value: string | null | undefined) => {
  return formatDateWithYear(value) || 'TBD';
};

export const AuditFindingFlags = ({violations}: {violations: RoadmapAuditFindingViolation[]}) => {
  const flagCount = violations.length;

  if (flagCount === 0) {
    return <Text color="subtle">—</Text>;
  }

  return (
    <Stack gap="8">
      {violations.map((violation, index) => (
        <Box
          key={`${violation.rule_id}-${index}`}
          display="flex"
          alignItems="center"
          className={styles.auditFindingsFlagCriterion}
        >
          <Box className={styles.auditFindingsFlagCriterionIcon}>
            <UIIcon src={CloseLine} aria-hidden="true" />
          </Box>
          <Text size="small" isBold>
            {violation.label}
          </Text>
        </Box>
      ))}
    </Stack>
  );
};

const AuditFindingGroupRow = ({
  node,
  depth,
  expanded,
  childrenId,
  domId,
  onToggle,
}: {
  node: AuditFindingHierarchyNode;
  depth: number;
  expanded: boolean;
  childrenId: string;
  domId?: string;
  onToggle: () => void;
}) => {
  const label = node.breadcrumb?.title ?? 'Unassigned hierarchy';
  const workstreamId = node.breadcrumb?.companyos_workstream_id;
  const labelContent = (
    <>
      <Text size="small" isBold>
        {label}
      </Text>
      {node.breadcrumb ? (
        <Text size="small" color="subtle">
          {node.breadcrumb.key}
        </Text>
      ) : null}
    </>
  );

  return (
    <Table.Row id={domId} className={styles.auditFindingsGroupRow}>
      <Table.Cell colSpan={AUDIT_FINDING_COLUMNS.length}>
        <div
          className={styles.auditFindingsGroupCell}
          style={{paddingLeft: Math.max(0, depth) * AUDIT_HIERARCHY_INDENT_PX}}
        >
          <IconButton
            variant="borderless"
            size="small"
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${label}`}
            aria-expanded={expanded}
            aria-controls={childrenId}
            onClick={onToggle}
          >
            <UIIcon src={expanded ? ChevronDownLine : ChevronRightLine} size="small" />
          </IconButton>
          <div className={styles.auditFindingsGroupLabel}>
            {workstreamId != null ? (
              <Link
                to={ROUTE_PATHS.WORKSTREAMS_DETAIL.replace(':id', String(workstreamId))}
                hasNoUnderline
                monochromatic
                className={styles.auditFindingsGroupLink}
              >
                {labelContent}
              </Link>
            ) : (
              <div className={styles.auditFindingsGroupLink}>{labelContent}</div>
            )}
            <Text size="small" color="subtle">
              {node.descendantCount} roadmap initiative
              {node.descendantCount === 1 ? '' : 's'}
            </Text>
          </div>
        </div>
      </Table.Cell>
    </Table.Row>
  );
};

const AuditFindingItemRow = ({
  item,
  depth,
  domId,
}: {
  item: RoadmapAuditFindingItem;
  depth: number;
  domId?: string;
}) => {
  const assignee = item.assignee;

  return (
    <Table.Row id={domId} data-testid={`audit-finding-${item.initiative.key}`}>
      <Table.Cell>
        <div
          className={styles.auditFindingsInitiativeCell}
          style={{
            paddingLeft:
              Math.max(0, depth) * AUDIT_HIERARCHY_INDENT_PX + AUDIT_HIERARCHY_TOGGLE_OFFSET_PX,
          }}
        >
          <RoadmapInitiativeLink
            title={item.initiative.title}
            jiraKey={item.initiative.key}
            jiraUrl={item.initiative.jira_url ?? null}
          />
        </div>
      </Table.Cell>
      <Table.Cell>
        <RoadmapPerson
          person={
            assignee
              ? {
                  name: assignee.display_name,
                  ldap: assignee.employee_ldap,
                }
              : null
          }
          emptyLabel="Unassigned"
        />
      </Table.Cell>
      <Table.Cell>
        <RoadmapJiraStatus status={item.initiative.status} />
      </Table.Cell>
      <Table.Cell>
        <Text size="small">{formatAuditFindingDate(item.initiative.start_date)}</Text>
      </Table.Cell>
      <Table.Cell>
        <Text size="small">{formatAuditFindingDate(item.initiative.end_date)}</Text>
      </Table.Cell>
      <Table.Cell>
        <AuditFindingFlags violations={item.violations} />
      </Table.Cell>
    </Table.Row>
  );
};

export const AuditFindingsTable = ({
  items,
  expansionKey,
  isLoading,
  nextCursor,
  isLoadingMore,
  onLoadMore,
}: {
  items: RoadmapAuditFindingItem[];
  expansionKey: string;
  isLoading?: boolean;
  nextCursor: string | null;
  isLoadingMore: boolean;
  onLoadMore: (cursor: string) => void;
}) => {
  const loadMoreSentinelRef = useRef<HTMLDivElement | null>(null);
  const requestedCursorRef = useRef<string | null>(null);
  const hierarchy = useMemo(() => buildAuditFindingHierarchy(items), [items]);
  const nodeIds = useMemo(() => getAuditHierarchyNodeIds(hierarchy), [hierarchy]);
  const nodeIdSignature = nodeIds.join('\u0000');
  const [expansion, setExpansion] = useState<{
    key: string;
    expanded: Set<string>;
    known: Set<string>;
  }>(() => ({
    key: expansionKey,
    expanded: new Set(nodeIds),
    known: new Set(nodeIds),
  }));
  const effectiveExpansion =
    expansion.key === expansionKey
      ? expansion
      : {
          key: expansionKey,
          expanded: new Set(nodeIds),
          known: new Set(nodeIds),
        };

  useEffect(() => {
    setExpansion((previous) => {
      if (previous.key !== expansionKey) {
        return {
          key: expansionKey,
          expanded: new Set(nodeIds),
          known: new Set(nodeIds),
        };
      }
      const expanded = new Set(previous.expanded);
      nodeIds.forEach((nodeId) => {
        if (!previous.known.has(nodeId)) expanded.add(nodeId);
      });
      return {key: expansionKey, expanded, known: new Set(nodeIds)};
    });
    // nodeIdSignature captures ordered membership while keeping this effect stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expansionKey, nodeIdSignature]);

  const visibleRows = flattenVisibleHierarchy(hierarchy, effectiveExpansion.expanded);
  const toggle = (nodeId: string) =>
    setExpansion((previous) => {
      const base = previous.key === expansionKey ? previous : effectiveExpansion;
      const expanded = new Set(base.expanded);
      if (expanded.has(nodeId)) {
        expanded.delete(nodeId);
      } else {
        expanded.add(nodeId);
      }
      return {...base, expanded};
    });

  useEffect(() => {
    requestedCursorRef.current = null;
  }, [nextCursor]);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !nextCursor || isLoading || isLoadingMore) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting || requestedCursorRef.current === nextCursor) return;
        requestedCursorRef.current = nextCursor;
        onLoadMore(nextCursor);
      },
      {rootMargin: '400px 0px'}
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [isLoading, isLoadingMore, nextCursor, onLoadMore]);

  return (
    <div className={styles.auditFindingsTableRegion}>
      {isLoading ? (
        <div
          className={styles.auditFindingsRefreshIndicator}
          data-testid="audit-findings-refresh-indicator"
          role="status"
          aria-label="Updating audit findings"
        >
          <Skeleton.Box height={2} style={{width: '100%'}} />
        </div>
      ) : null}
      <div
        className={styles.auditFindingsTableShell}
        data-testid="audit-findings-table-scroll"
        role="region"
        aria-label="Audit findings table"
        tabIndex={0}
        aria-busy={isLoading || isLoadingMore}
      >
        <Table<RoadmapAuditFindingItem>
          columns={AUDIT_FINDING_COLUMNS}
          data={items}
          border={false}
          resizable={false}
          headerTextSize="small"
          columnLabelOverrides={AUDIT_FINDING_COLUMN_LABELS}
          columnHeaderLabel={AUDIT_FINDING_COLUMN_HEADERS}
          className={styles.auditFindingsTable}
        >
          <Table.Header data-testid="audit-findings-table-header" />
          <Table.Body className={styles.tableBody}>
            {visibleRows.map((row) =>
              row.type === 'group' ? (
                <AuditFindingGroupRow
                  key={`group-${row.node.id}`}
                  node={row.node}
                  depth={row.depth}
                  expanded={effectiveExpansion.expanded.has(row.node.id)}
                  childrenId={row.childrenId}
                  domId={row.domId}
                  onToggle={() => toggle(row.node.id)}
                />
              ) : (
                <AuditFindingItemRow
                  key={`item-${row.item.initiative.key}`}
                  item={row.item}
                  depth={row.depth}
                  domId={row.domId}
                />
              )
            )}
          </Table.Body>
        </Table>
        {!isLoading && nextCursor ? (
          <div
            ref={loadMoreSentinelRef}
            className={styles.auditFindingsLoadSentinel}
            data-testid="audit-findings-load-sentinel"
            role={isLoadingMore ? 'status' : undefined}
            aria-label={isLoadingMore ? 'Loading more audit findings' : undefined}
          >
            {isLoadingMore ? <Skeleton.Box height={16} width={120} /> : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};
